import React, { useState, useEffect } from 'react';
import styles from './UserInfo.module.css';
import { CustomTitle, CustomInputText, CustomButton, CustomLoader, CustomAlert, CustomNotification } from '../../../components';
import { useTranslation } from 'react-i18next';
import { getUserInfos, updatePassword } from '../../../services/userService';
import { deleteAccount } from '../../../services/authService';
import { useDispatch } from 'react-redux';
import { logoutRdx } from '../../../store/authSlice';
import { useNavigate } from 'react-router-dom';

const UserInfo = () => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [userId, setUserId] = useState(0);
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const [isWrongPassword, setIsWrongPassword] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [notificationMsg, setNotificationMsg] = useState('');

    const [isLoading, setIsLoading] = useState(true);
    const [isAlertOpen, setIsAlertOpen] = useState(false);

    useEffect(() => {
        handleGetUserInfos();
    }, []);

    const handleGetUserInfos = async () => {
        setIsLoading(true);
        
        const data = await getUserInfos();

        setUserId(data.id);
        setUsername(data.username);
        setEmail(data.email);

        setIsLoading(false);
    }

    const isPasswordSame = () => {
        return newPassword === confirmNewPassword;
    }

    const isNewPasswordOk = () => {
        return newPassword === "" || (newPassword.length >= 6 && password !== newPassword);
    }

    const isFormComplete = () => {
        return (
            password !== "" &&
            newPassword !== "" &&
            isNewPasswordOk() &&
            isPasswordSame()
        )
    }

    const showNotification = (message: string) => {
        setNotificationMsg(message);
        setIsNotificationOpen(true);
    }

    const handleUpdateUserInfos = async () => {
        setIsLoading(true);

        if (isFormComplete()) {
            const res = await updatePassword(password, newPassword, confirmNewPassword);

            setPassword('');
            setNewPassword('');
            setConfirmNewPassword('');

            if (res) {
                showNotification(t('account.informations.success_update_password'));
            } else {
                setIsWrongPassword(true);
                showNotification(t('account.informations.wrong_password'));
            }
        }

        setIsLoading(false);
    }

    const handleDeleteAccount = async () => {
        setIsAlertOpen(false);
        setIsLoading(true);
        
        const res = await deleteAccount(userId);
        if (res) {
            await dispatch(logoutRdx());
            setIsLoading(false);
            navigate('/login');
        } else {
            setIsLoading(false);
        }
    }

    return (
        <div className={styles.container}>
            { !isLoading &&
                <>
                    <CustomTitle className={styles.title}>{t('account.informations.title')}</CustomTitle>

                    <CustomInputText id="username" label={t('signin.username')} type="text" textValue={username} setTextValue={setUsername} disabled/>
                    <CustomInputText id="email" label={t('signin.email')} type="email" textValue={email} setTextValue={setEmail} disabled />

                    <CustomInputText id="password" error={isWrongPassword} label={t('signin.password')} type="password" textValue={password} setTextValue={setPassword} onChange={() => isWrongPassword && setIsWrongPassword(false)} />
                    <CustomInputText id="newPassword" error={!isNewPasswordOk()} label={t('account.informations.new_password')} type="password" textValue={newPassword} setTextValue={setNewPassword} helperText={!isNewPasswordOk() ? t('account.informations.error_password') : ''} />
                    <CustomInputText id="newPasswordConfirm" error={!isPasswordSame()} label={t('account.informations.new_password_confirm')} type="password" textValue={confirmNewPassword} setTextValue={setConfirmNewPassword} helperText={!isPasswordSame() ? t('signin.error_password_confirm') : ''}/>

                    <CustomButton className={styles.btn} onClick={() => handleUpdateUserInfos()} disabled={!isFormComplete()}>{t('general.save')}</CustomButton>
                    <CustomButton className={styles.btn} color="error" onClick={() => { setIsAlertOpen(true) }}>{ t('account.informations.delete_account') }</CustomButton>

                    <CustomAlert
                        isOpen={isAlertOpen}
                        onValid={() => { handleDeleteAccount() }}
                        onClose={() => { setIsAlertOpen(false) }}
                        title={ t('account.informations.delete_account') }
                        content={ t('account.informations.delete_account_confirmation') }
                    />
                </>
            }

            <CustomLoader isLoading={isLoading} loaderType='skeleton' />

            <CustomNotification
                message={notificationMsg}
                isOpen={isNotificationOpen}
                onClose={() => setIsNotificationOpen(false)}
            />
        </div>
    )
}

export default UserInfo;